import React, { useEffect, useRef } from "react";
import headerImage from "./assets/vitrine/industry/header.png";
import section1Video from "./assets/vitrine/industry/indus1.mp4";
import section2Image from "./assets/vitrine/industry/section2.png";
import section3Image from "./assets/vitrine/industry/section3.png";
import section4Video from "./assets/vitrine/industry/section4.mp4";
import section5Image from "./assets/vitrine/industry/section5.png";
import "./IndustryPage.css";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.akremanaya.weavevomobile&hl=fr";

function HoverVideo({ src, wide = false }) {
  const ref = useRef(null);

  const playWithSound = () => {
    const video = ref.current;
    if (!video) return;
    video.muted = false;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  };

  const keepPlayingMuted = () => {
    const video = ref.current;
    if (!video) return;
    video.muted = true;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  };

  return (
    <div
      className={wide ? "industry-video industry-videoWide" : "industry-video"}
      onMouseEnter={playWithSound}
      onMouseLeave={keepPlayingMuted}
    >
      <video ref={ref} autoPlay muted loop playsInline controls preload="auto">
        <source src={src} type="video/mp4" />
      </video>
    </div>
  );
}

function Breakdown({ fieldChallenge, guided, advantage }) {
  return (
    <div className="industry-breakdown">
      <h3>Operational Breakdown</h3>
      <p><strong>The Field Challenge:</strong> {fieldChallenge}</p>
      <p><strong>Guided Co-Creation:</strong> {guided}</p>
      <p><strong>The Living Weave Advantage:</strong> {advantage}</p>
    </div>
  );
}

function Metrics({ items }) {
  return (
    <div className="industry-metricsBlock">
      <h3>Key Performance Metrics</h3>
      <div className="industry-metrics">
        {items.map(([label, text]) => (
          <article key={label}>
            <strong>{label}</strong>
            <span>{text}</span>
          </article>
        ))}
      </div>
    </div>
  );
}

export default function IndustryPage() {
  useEffect(() => {
    document.title = "Your Plants Evolve Continuously. Your Standards Must Too.";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="industry-page">
      <section className="industry-hero">
        <img src={headerImage} alt="" />
        <div className="industry-heroOverlay" />
        <div className="industry-heroCopy">
          <h1>Your Plants Evolve Continuously. Your Standards Must Too.</h1>
          <p>Weavevo bridges process engineering and frontline tradecraft into a modular video framework that moves at the speed of your production lines.</p>
        </div>
      </section>

      <section className="industry-section industry-sectionSplit">
        <div className="industry-copy">
          <span className="industry-category">Manufacturing &amp; Process</span>
          <h2>Standard Operating Procedures Workers Actually Use—Always Accurate on the Line.</h2>
          <p className="industry-subtitle">Eliminate documentation obsolescence: transform 60-page static binders into modular, step-by-step video SOPs, filmed on the shop floor by operators and updated on the fly.</p>
          <Breakdown
            fieldChallenge="Heavy binders gather dust on workbenches, and intranet PDFs are rarely opened during shifts. Conventional training videos become obsolete the moment an engineer adjusts a single tool or sensor, as nobody has the time or budget to re-edit a full master cut."
            guided="The process engineer structures the procedure into six micro-slots (1. Lockout/tagout, 2. Chuck changeover, 3. Optical calibration, 4. Cycle initialization, 5. Dimensional check, 6. Workstation clean-down). Lead operators capture each task directly on the line with the mobile app."
            advantage="A torque setting changes or a new safety guard is installed? Replace only the relevant 20-second slot in seconds. The stream refreshes instantly: the laser-engraved QR code on the machine never changes, perpetually routing operators to the latest validated standard."
          />
          <Metrics
            items={[
              ["Zero Re-Printing:", "Permanent, machine-mounted QR codes remain valid indefinitely."],
              ["< 30-Second Turnaround:", "Time required to hot-swap an outdated step with a validated take."],
              ["No Video Editing Suite:", "Automated audio leveling and transition assembly via The Loom."],
              ["Audit-Ready Compliance:", "Certified timestamps for every revision, ready for ISO and internal audits."],
            ]}
          />
        </div>
        <HoverVideo src={section1Video} />
      </section>

      <section className="industry-section industry-sectionSplit industry-sectionReverse">
        <figure className="industry-media">
          <img src={section2Image} alt="" loading="lazy" />
        </figure>
        <div className="industry-copy">
          <h2>Zero Handoff Friction: 90-Second Visual Shift Logs for 24/7 Operations.</h2>
          <p className="industry-subtitle">Replace scribbled paper logbooks and overlooked emails with structured, timestamped video handovers your incoming shift can digest instantly.</p>
          <Breakdown
            fieldChallenge="Shift handovers across 2x8 and 3x8 rotations regularly leak vital operational context. Unreported micro-stoppages, abnormal machine hums, and ad-hoc mechanical adjustments go unrecorded, slowing down startup routines for incoming crews."
            guided="Fifteen minutes before shift end, the outgoing supervisor and line operators capture four chronological video slots: observed line anomalies, running cycle speed, active maintenance interventions, and raw material replenishment status."
            advantage="The incoming crew reviews the unified 90-second brief directly on their shop-floor tablet at the shift briefing station. The feed updates seamlessly shift after shift without inbox clutter, backed by tamper-evident timestamps for complete operational traceability."
          />
          <Metrics
            items={[
              ["90-Second Debrief:", "Total review time required to take over the line with full situational awareness."],
              ["100% Visual Context:", "Direct evidence of machine alarms, physical defects, and gauge readings."],
              ["Zero Inbox Clutter:", "One persistent timeline refreshed cycle after cycle."],
              ["Audit-Ready Traceability:", "Tamper-evident historical logs for post-incident root cause analysis."],
            ]}
          />
        </div>
      </section>

      <section className="industry-section industry-sectionSplit">
        <div className="industry-copy">
          <h2>Remote Guided Diagnostics: Bring Centralized Expertise to Every Production Line.</h2>
          <p className="industry-subtitle">Slash Mean Time to Repair (MTTR): replace vague phone calls with standardized visual triage templates orchestrated by machine specialists.</p>
          <Breakdown
            fieldChallenge="When a critical asset trips, an on-site technician wastes hours describing symptoms over the phone to a centralized OEM specialist or remote engineer. Verbal misunderstandings and trial-and-error troubleshooting prolong costly unplanned downtime."
            guided="The remote expert instantly deploys a structured four-slot investigation template (1. Pressure gauge reading, 2. Bearing acoustic signature and vibration, 3. PLC status indicators, 4. Hydraulic filter condition). The field technician records each checkpoint in minutes using the mobile app."
            advantage="Upon reviewing the high-definition feeds, the expert isolates the root cause and injects the exact video tutorial for the corrective procedure directly into the Weave. Once resolved, the module integrates into the CMMS knowledge base to accelerate future troubleshooting."
          />
          <Metrics
            items={[
              ["50% MTTR Reduction:", "Immediate root-cause diagnosis without dispatching traveling field engineers."],
              ["Acoustic & Optical Clarity:", "Lossless mobile capture to analyze indicator lights and machine noise patterns."],
              ["Direct Solution Injection:", "Corrective repair steps embedded inside the existing video container."],
              ["CMMS Knowledge Capture:", "Real-world incident records instantly turned into repeatable training assets."],
            ]}
          />
        </div>
        <figure className="industry-media">
          <img src={section3Image} alt="" loading="lazy" />
        </figure>
      </section>

      <section className="industry-section industry-sectionVideoLead">
        <HoverVideo src={section4Video} wide />
        <div className="industry-copy industry-copyWide">
          <h2>Site Safety Inductions Tailored to Real-Time Hazards—Zero Stale Footage.</h2>
          <p className="industry-subtitle">Eliminate gatehouse bottlenecks: replace generic 25-minute induction tapes with modular HSE walk-throughs updated instantly as daily site conditions shift.</p>
          <Breakdown
            fieldChallenge="Forcing contractors and temporary workers through generic 25-minute safety videos creates long queues at the security gate and fails to communicate active, high-risk work zones or same-day co-activity hazards."
            guided="The EHS manager designs a structured path broken into targeted micro-modules (mandatory PPE, traffic routing, hot work permits, chemical storage, evacuation protocols). Safety officers capture genuine footage directly on the shop floor."
            advantage="A new scaffolding zone opens or heavy crane lifting begins at the loading dock? The safety officer inserts or swaps a hazard alert slot in under two minutes. The QR code on the safety passport badge remains identical, instantly delivering today's validated site rules."
          />
          <Metrics
            items={[
              ["Induction Time Cut by 3x:", "Focused 5- to 7-minute visual briefings completed without gatehouse delays."],
              ["2-Minute Hazard Updates:", "Instantaneous deployment of active co-activity and maintenance alerts."],
              ["Tamper-Evident Compliance:", "Certified, timestamped completion records for every outside contractor."],
              ["Zero Badge Re-Issuing:", "Permanent access passes and QR codes remain valid across multi-year contracts."],
            ]}
          />
        </div>
      </section>

      <section className="industry-section industry-sectionSplit industry-sectionReverse">
        <figure className="industry-media">
          <img src={section5Image} alt="" loading="lazy" />
        </figure>
        <div className="industry-copy">
          <h2>Log Near-Miss Hazards in 10 Seconds. Stop Incidents Before They Happen.</h2>
          <p className="industry-subtitle">Eliminate reporting friction: replace tedious incident logs with rapid mobile video capture, instantly turned into actionable visual case studies for shop-floor toolbox talks.</p>
          <Breakdown
            fieldChallenge="Most near-misses and unsafe conditions (blocked exits, hydraulic fluid leaks, improper slinging) go unreported. Technicians and operators bypass paper forms and clunky software portals because documenting an issue interrupts their daily workflow."
            guided="The EHS lead initiates a targeted reporting drive (Seed layout): line operators and technicians record observed hazards in ten seconds using their mobile devices, capturing authentic visual context on the spot."
            advantage="Once the hazard is mitigated, the supervisor validates a paired clip showing the corrected setup. The Weave instantly becomes an interactive, evolving catalog of real-world lessons learned, ready to display during shift safety briefings without compiling PowerPoint decks."
          />
          <Metrics
            items={[
              ["10-Second Logging:", "Instant hazard recording on the line with zero administrative delay."],
              ["4x Increase in Hazard Reporting:", "Language and typing barriers eliminated across diverse teams."],
              ["Closed-Loop Resolution:", "Verified before/after video confirmation for every logged near-miss."],
              ["Impactful Toolbox Talks:", "Discussion material built directly from plant-floor reality."],
            ]}
          />
        </div>
      </section>

      <section className="industry-section industry-sectionPlain">
        <div className="industry-copy industry-copyWide">
          <h2>Living 5S Standards on the Shop Floor: Make Kaizen Stick Without Laminated Sheets.</h2>
          <p className="industry-subtitle">Sustain continuous improvement: replace fading paper checklists with dynamic visual standards owned by operators and updated in 30 seconds.</p>
          <Breakdown
            fieldChallenge="Most 5S rollouts fade within months. Laminated photos taped to workbenches peel and degrade, while updating binders for minor shadow board or bin layout tweaks is too cumbersome to maintain."
            guided="During Kaizen events, the Lean champion and cell operators capture the baseline condition (clutter, wasted motion) and document the optimized target standard (shadow boards, color coding, streamlined part flows) directly on mobile devices."
            advantage="Displayed on workstation tablets, the standard stays interactive. When the team discovers an ergonomic tweak or layout improvement months later, the lead hot-swaps the relevant 15-second clip. Lean standards remain living practices adapted by the team, rather than forgotten administrative mandates."
          />
          <Metrics
            items={[
              ["100% Frontline Ownership:", "Standards authored and filmed directly by machine operators."],
              ["Zero Paper Clutter:", "Laminated sheets replaced by persistent digital workstation displays."],
              ["30-Second Iteration:", "Micro-optimizations updated instantly across the shop floor."],
              ["Streamlined 5S Audits:", "Instant visual gap analysis between actual workstation state and the master standard."],
            ]}
          />
        </div>
      </section>

      <section className="industry-cta">
        <h2>Your Plant&apos;s Know-How Is Alive. Your Standards Must Be Too.</h2>
        <p>Stop trapping shop-floor mastery in static binders: build an enduring operational asset with your workforce, continuously refined by the people who make your products.</p>
        <div className="industry-storeActions">
          <a href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer" className="industry-storeButton industry-storeButtonPrimary">
            <i className="bi bi-google-play" aria-hidden="true" />
            <span>Play Store</span>
          </a>
          <button type="button" className="industry-storeButton industry-storeButtonSecondary" disabled>
            <i className="bi bi-apple" aria-hidden="true" />
            <span>App Store</span>
          </button>
        </div>
      </section>
    </main>
  );
}
